/**
 * SharedReplay - Shared yearly timeline of both partners' adventures
 */
import { useState, useEffect } from "react";
import { Calendar, Play, Loader2, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function SharedReplay() {
  const { token } = useAuth();
  const { hasDuoAccess } = useEntitlements();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    fetchReplay();
  }, [year]);

  const fetchReplay = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/premium/duo/replay?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Failed to fetch shared replay");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  if (!data?.is_duo) {
    return (
      <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
        <CardContent className="p-6 text-center">
          <Play className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h3 className="font-serif text-lg font-semibold mb-2">Shared Yearly Replay</h3>
          <p className="text-sm text-muted-foreground">{data?.message || "Link a partner for shared replay"}</p>
        </CardContent>
      </Card>
    );
  }

  if (!hasDuoAccess) {
    return (
      <>
        <Card className="relative overflow-hidden border-emerald-500/20">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
            <Lock className="w-8 h-8 text-emerald-500 mb-2" />
            <p className="text-sm font-medium">Duo Plan Required</p>
            <button onClick={() => setShowPaywall(true)} className="mt-2 px-4 py-1.5 bg-emerald-500 text-white text-sm rounded-full">Learn more on duolyst.co.uk</button>
          </div>
          <CardContent className="p-6 blur-sm"><div className="h-32" /></CardContent>
        </Card>
        {showPaywall && <PaywallScreen type={PaywallType.DUO_UPSELL} isOpen={showPaywall} onClose={() => setShowPaywall(false)} />}
      </>
    );
  }

  const monthlySummary = data.monthly_summary || {};

  return (
    <div className="space-y-4" data-testid="shared-replay">
      {/* Year Selector */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setYear(y => y - 1)}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="font-serif text-lg font-semibold">{year} Together</h3>
        <Button variant="ghost" size="sm" onClick={() => setYear(y => y + 1)} disabled={year >= new Date().getFullYear()}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {data.total_adventures} adventures with {data.partner_name}
      </p>

      {/* Monthly Timeline */}
      <div className="space-y-2">
        {MONTHS.map((month, idx) => {
          const monthData = monthlySummary[idx + 1];
          if (!monthData) return null;
          return (
            <Card key={idx}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-xs">{month}</Badge>
                  <span className="text-xs text-muted-foreground">{monthData.count} adventure{monthData.count !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-1">
                  {monthData.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full ${item.who === data.partner_name ? 'bg-pink-500' : 'bg-primary'}`} />
                      <span className="text-foreground truncate flex-1">{item.title}</span>
                      <span className="text-xs text-muted-foreground">{item.who?.split(' ')[0]}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {data.total_adventures === 0 && (
        <p className="text-center text-sm text-muted-foreground py-4">No adventures recorded for {year}</p>
      )}
    </div>
  );
}
